/**
 * Shared shape for the recurring "empty PLP" Sentry signal (ECOM-2634 / ECOM-2692).
 *
 * Pure (no Nuxt/Sentry imports) so the reason set and payload shape are
 * unit-testable. Callers pass the built payload to `Sentry.captureMessage`.
 */
export const PLP_EMPTY_REASON = {
  /** Odoscope query had no resolvable categoryId — bailed to the storefront listing (ECOM-2634). */
  missingCategory: 'missing-category',
  /** Odoscope returned zero products with no applied filter (ECOM-2634). */
  emptyOdoscopeResponse: 'empty-odoscope-response',
  /**
   * Post-hydration reconciliation detected a stranded PLP (category never loaded
   * for the active route) and is healing it — the browser-back hydration race
   * (ECOM-2692). Lets us confirm the race occurs in production and that the fix
   * fires, since the timing window resists deterministic reproduction.
   */
  hydrationRaceReconciled: 'hydration-race-reconciled',
} as const

export type PlpEmptyReason =
  (typeof PLP_EMPTY_REASON)[keyof typeof PLP_EMPTY_REASON]

export type PlpEmptyReport = {
  message: string
  level: 'warning'
  tags: { feature: 'plp-odoscope'; plp_empty_reason: PlpEmptyReason }
  extra: Record<string, unknown>
}

export const buildPlpEmptyReport = (
  reason: PlpEmptyReason,
  extra: Record<string, unknown>,
  // The `hydration-race-reconciled` signal fires on a successful *heal*, not on
  // an empty render, so its caller passes a message that reads accurately
  // ("...race healed"). The empty-PLP callers omit this and get the default.
  // The `plp_empty_reason` tag is kept for all reasons so one Sentry filter
  // surfaces the whole empty-PLP cluster (incl. the heal events).
  messageOverride?: string,
): PlpEmptyReport => ({
  message: messageOverride ?? `PLP empty (odoscope): ${reason}`,
  level: 'warning',
  tags: { feature: 'plp-odoscope', plp_empty_reason: reason },
  extra,
})
